import React, { useState, useEffect } from "react";
import {
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase-config"; // Import your Firebase config
import { useTranslation } from "react-i18next";
import { ColorRing } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";

function UserSettingsPage() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordUpdateMessage, setPasswordUpdateMessage] = useState("");
  const [passwordUpdateError, setPasswordUpdateError] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [emailUpdateMessage, setEmailUpdateMessage] = useState("");
  const [emailUpdateError, setEmailUpdateError] = useState("");

  const [firstName, setFirstName] = useState(""); // For updating first name
  const [lastName, setLastName] = useState(""); // For updating last name
  const [updateProfileMessage, setUpdateProfileMessage] = useState("");
  const [updateProfileError, setUpdateProfileError] = useState("");
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);

  const [logoutLoading, setLogoutLoading] = useState(false);
  const [integrations, setIntegrations] = useState({
    google: false,
    microsoft: false,
    loading: true,
  });
  const [integrationError, setIntegrationError] = useState(null);

  const { t } = useTranslation("UserProfile");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "User Profile Page - ProRecruit"; // Set the title dynamically

    // Cleanup function: Reset the title when the component unmounts
    return () => {
      document.title = "ProRecruit"; // Reset to a default title
    };
  }, []); // Empty dependency array: runs only on mount (and cleanup on unmount)

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.email);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const data = userDocSnap.data().userData;
            setUserData(data);
            setFirstName(data.firstName || ""); // Initialize
            setLastName(data.lastName || ""); // Initialize
          } else {
            setError("User data not found.");
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          setError("Failed to load user data.");
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, []);
  // Fetch integration status on load
  useEffect(() => {
    const fetchStatus = async () => {
      if (!user) return; // Don't run if user isn't loaded
      setIntegrations((prev) => ({ ...prev, loading: true }));
      try {
        // --- Method 1: Assuming a REST API backend ---
        const idToken = await user.getIdToken();
        const response = await fetch("/api/integrations/status", {
          // Your backend status endpoint
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch integration status");
        }
        const statusData = await response.json();
        setIntegrations({
          google: !!statusData.google,
          microsoft: !!statusData.microsoft,
          loading: false,
        });

        // --- Method 2: Assuming Firebase Functions backend ---
        // const functions = getFunctions();
        // const getIntegrationStatus = httpsCallable(functions, 'getIntegrationStatus'); // Your callable function name
        // const result = await getIntegrationStatus();
        // const statusData = result.data;
        // setIntegrations({ google: !!statusData.google, microsoft: !!statusData.microsoft, loading: false });
      } catch (err) {
        console.error("Error fetching integration status:", err);
        setIntegrationError("Could not load integration status.");
        setIntegrations({ google: false, microsoft: false, loading: false });
      }
    };

    fetchStatus();
  }, [user]); // Re-fetch if user changes

  // --- Handlers for Connection/Disconnection ---

  const handleConnect = async (provider) => {
    console.log(`Connecting ${provider}...`);
    if (!user) {
      setIntegrationError("Please wait, user data not loaded yet.");
      return;
    }

    setIntegrationError(null); // Clear previous errors
    setIntegrations((prev) => ({ ...prev, loading: true })); // Show loading state

    try {
      const idToken = await user.getIdToken(); // Get the current user's token

      // Make a fetch request to your backend's connect endpoint
      const response = await fetch(`/api/connect/${provider}`, {
        method: "GET", // Method should match your backend route definition
        headers: {
          Authorization: `Bearer ${idToken}`, // Send the token
          "Content-Type": "application/json",
          // Add other headers if necessary
        },
      });
      console.log(response.status);
      const data = await response.json(); // Expecting JSON back { authUrl: "..." }
      console.log(data);
      if (!response.ok) {
        // Handle errors returned from the backend
        throw new Error(
          data.error || `Failed to initiate ${provider} connection.`
        );
      }

      if (data.authUrl) {
        // Redirect the user's browser to the URL received from the backend
        window.location.href = data.authUrl;
      } else {
        throw new Error(
          `Backend did not return an authorization URL for ${provider}.`
        );
      }
    } catch (err) {
      console.error(`Error connecting ${provider}:`, err);
      setIntegrationError(
        err.message || `Failed to connect ${provider}. Please try again.`
      );
      setIntegrations((prev) => ({ ...prev, loading: false })); // Turn off loading on error
    }
    // No need for finally block to turn off loading here, as success results in navigation away
  };

  const handleDisconnect = async (provider) => {
    setIntegrations((prev) => ({ ...prev, loading: true })); // Indicate loading
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`/api/disconnect/${provider}`, {
        // Your backend disconnect endpoint
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to disconnect ${provider}`);
      }
      // Update state after successful disconnect
      setIntegrations((prev) => ({
        ...prev,
        [provider]: false,
        loading: false,
      }));

      // --- Firebase Functions approach ---
      // const functions = getFunctions();
      // const disconnectProvider = httpsCallable(functions, `disconnect${provider.charAt(0).toUpperCase() + provider.slice(1)}`);
      // await disconnectProvider();
      // setIntegrations(prev => ({ ...prev, [provider]: false, loading: false }));
    } catch (err) {
      console.error(`Error disconnecting ${provider}:`, err);
      setIntegrationError(
        `Failed to disconnect ${provider}. Please try again.`
      );
      // Optionally refetch status or revert loading state
      setIntegrations((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await signOut(auth);
      navigate("/login"); // Redirect to login page
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDashboard = async () => {
    setLogoutLoading(true);
    try {
      navigate("/dashboard"); // Redirect to login page
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    setPasswordUpdateError("");
    setPasswordUpdateMessage("");

    if (newPassword !== confirmNewPassword) {
      setPasswordUpdateError(t("passwordMismatch"));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordUpdateError(t("weakPassword"));
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordUpdateMessage(t("passwordUpdated"));
      setIsEditingPassword(false); // Exit edit mode on success
    } catch (error) {
      console.error("Password update error:", error);
      switch (error.code) {
        case "auth/wrong-password":
          setPasswordUpdateError(t("incorrectPassword"));
          break;
        case "auth/weak-password":
          setPasswordUpdateError(t("weakPassword"));
          break;
        default:
          setPasswordUpdateError(t("passwordUpdateFailed") + error.message);
      }
    }
  };

  const handleEmailUpdate = async () => {
    setEmailUpdateError("");
    setEmailUpdateMessage("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailUpdateError(t("invalidEmail"));
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      ); //Reauthenticate.
      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);
      //Update on firestore as well
      const userDocRef = doc(db, "users", user.email);
      await updateDoc(userDocRef, { "userData.email": newEmail }); // Critical: Update the user's document.
      //todo: Option to send a new email to verify new email.
      setEmailUpdateMessage(t("emailUpdated")); // Show success
      setIsEditingEmail(false);
    } catch (error) {
      console.error("Email update error:", error);
      switch (error.code) {
        case "auth/wrong-password":
          setEmailUpdateError(t("incorrectPassword"));
          break;
        case "auth/email-already-in-use":
          setEmailUpdateError(t("emailInUse"));
          break;
        case "auth/invalid-email":
          setEmailUpdateError(t("invalidEmail"));
          break;
        default:
          setEmailUpdateError(t("emailUpdateFailed") + error.message);
      }
    }
  };

  const handleProfileUpdate = async () => {
    setUpdateProfileError("");
    setUpdateProfileMessage("");
    try {
      const userDocRef = doc(db, "users", user.email); //Use email since it is the ID
      await updateDoc(userDocRef, {
        "userData.firstName": firstName,
        "userData.lastName": lastName,
        "userData.displayName": `${firstName} ${lastName}`, // Update displayName as well
      });
      setUpdateProfileMessage(t("profileUpdated")); // Use i18next
      setIsEditingProfile(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setUpdateProfileError(t("profileUpdateFailed") + error.message); // Use i18next.
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ColorRing
          visible={true}
          height="80"
          width="80"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
        />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center mt-4">{error}</div>;
  }

  if (!user || !userData) {
    return (
      <div className="text-center mt-4">Please log in to view settings.</div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <h1 className="text-3xl font-bold text-center mb-6">
        {t("userSettings")}
      </h1>

      {/* Profile Section */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-xl font-semibold mb-4">{t("profile")}</h2>
        {!isEditingProfile && (
          <button
            onClick={() => setIsEditingProfile(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {t("editProfile")}
          </button>
        )}

        {isEditingProfile ? (
          <>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="firstName"
              >
                {t("firstName")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed leading-tight focus:outline-none focus:shadow-outline"
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="lastName"
              >
                {t("lastName")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed leading-tight focus:outline-none focus:shadow-outline"
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="button"
                onClick={handleProfileUpdate}
              >
                {t("saveProfile")}
              </button>

              <button
                onClick={() => setIsEditingProfile(false)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ml-2"
              >
                {t("cancel")}
              </button>
            </div>
            {updateProfileMessage && (
              <p className="text-green-500 text-sm mt-2">
                {updateProfileMessage}
              </p>
            )}
            {updateProfileError && (
              <p className="text-red-500 text-sm mt-2">{updateProfileError}</p>
            )}
          </>
        ) : (
          <>
            <p className="text-slate-600 leading-relaxed mb-2">
              <strong>{t("firstName")}:</strong> {userData.firstName}
            </p>
            <p className="text-slate-600 leading-relaxed mb-2">
              <strong>{t("lastName")}:</strong> {userData.lastName}
            </p>
          </>
        )}
      </div>

      {/* Email Update Section */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-xl font-semibold mb-4">{t("email")}</h2>

        {!isEditingEmail && (
          <button
            onClick={() => setIsEditingEmail(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {t("changeEmail")}
          </button>
        )}

        {isEditingEmail ? (
          <>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="newEmail"
              >
                {t("newEmail")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed leading-tight focus:outline-none focus:shadow-outline"
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="currentPasswordEmail"
              >
                {t("currentPassword")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed mb-3 leading-tight focus:outline-none focus:shadow-outline"
                id="currentPasswordEmail"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="button"
                onClick={handleEmailUpdate}
              >
                {t("updateEmail")}
              </button>
              <button
                onClick={() => setIsEditingEmail(false)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ml-2"
              >
                {t("cancel")}
              </button>
            </div>

            {emailUpdateMessage && (
              <p className="text-green-500 text-sm mt-2">
                {emailUpdateMessage}
              </p>
            )}
            {emailUpdateError && (
              <p className="text-red-500 text-sm mt-2">{emailUpdateError}</p>
            )}
          </>
        ) : (
          <p className="text-slate-600 leading-relaxed mb-2">
            <strong>{t("email")}:</strong> {userData.email}
          </p>
        )}
      </div>

      {/* Password Update Section */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-xl font-semibold mb-4">{t("password")}</h2>

        {!isEditingPassword && (
          <button
            onClick={() => setIsEditingPassword(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {t("changePassword")}
          </button>
        )}

        {isEditingPassword ? (
          <>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="currentPassword"
              >
                {t("currentPassword")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed leading-tight focus:outline-none focus:shadow-outline"
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="newPassword"
              >
                {t("newPassword")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed leading-tight focus:outline-none focus:shadow-outline"
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="mb-6">
              <label
                className="block text-slate-600 leading-relaxed text-sm font-bold mb-2"
                htmlFor="confirmNewPassword"
              >
                {t("confirmNewPassword")}
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-slate-600 leading-relaxed mb-3 leading-tight focus:outline-none focus:shadow-outline"
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="button"
                onClick={handlePasswordUpdate}
              >
                {t("updatePassword")}
              </button>
              <button
                onClick={() => setIsEditingPassword(false)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ml-2"
              >
                {t("cancel")}
              </button>
            </div>
            {passwordUpdateMessage && (
              <p className="text-green-500 text-sm mt-2">
                {passwordUpdateMessage}
              </p>
            )}
            {passwordUpdateError && (
              <p className="text-red-500 text-smmt-2">{passwordUpdateError}</p>
            )}
          </>
        ) : (
          <p className="text-slate-600 leading-relaxed mb-2">
            <strong>{t("currentPassword")}:</strong> ********{" "}
            {/* Never display the actual password */}
          </p>
        )}
      </div>
      {/* Integrations Section */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <h2 className="text-xl font-semibold mb-4">{t("integrations")}</h2>
        {integrationError && (
          <p className="text-red-500 text-sm mb-4">{integrationError}</p>
        )}

        {integrations.loading ? (
          <div className="flex justify-center items-center">
            <ColorRing /* ... props ... */ />
          </div>
        ) : (
          <>
            {/* Google Integration */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="text-lg font-medium mb-2">Google Workspace</h3>
              {integrations.google ? (
                <div>
                  <p className="text-green-600 mb-2">Connected</p>
                  <button
                    onClick={() => handleDisconnect("google")}
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm focus:outline-none focus:shadow-outline"
                  >
                    {t("disconnect")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleConnect("google")}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm focus:outline-none focus:shadow-outline"
                >
                  {t("connectGoogle")}
                </button>
              )}
              {/* Add more details here if needed, e.g., which services are connected */}
            </div>

            {/* Microsoft 365 Integration */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Microsoft 365</h3>
              {integrations.microsoft ? (
                <div>
                  <p className="text-green-600 mb-2">Connected</p>
                  <button
                    onClick={() => handleDisconnect("microsoft")}
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm focus:outline-none focus:shadow-outline"
                  >
                    {t("disconnect")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleConnect("microsoft")}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm focus:outline-none focus:shadow-outline"
                >
                  {t("connectMicrosoft")}
                </button>
              )}
              {/* Add more details here if needed */}
            </div>

            {/* Add sections for other providers similarly */}
          </>
        )}
      </div>
      {/* Logout Button */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <button
          onClick={handleDashboard}
          className="bg-green-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          disabled={logoutLoading}
        >
          Dashboard
        </button>
        <button
          onClick={handleLogout}
          className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          disabled={logoutLoading}
        >
          {logoutLoading ? (
            <ColorRing
              visible={true}
              height="20"
              width="20"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          ) : (
            t("logout")
          )}
        </button>
      </div>
    </div>
  );
}

export default UserSettingsPage;
